'use client';

import * as React from 'react';

import type { AriaButtonProps } from '@react-aria/button';
import { Loader2 } from 'lucide-react';
import type { ClassValue, VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { UnstyledButton } from '../unstyled-button';

const buttonVariants = tv({
  base: 'inline-flex group flex-nowrap items-center justify-center gap-2 border-0 transition-all disabled:cursor-not-allowed active:translate-y-0.5 disabled:transform-none data-[loading=true]:pointer-events-none data-[loading=true]:opacity-80 focus:outline-1 focus:outline-offset-1 focus:outline-primary-300',
  variants: {
    variant: {
      primary:
        'bg-primary-500 text-base-white hover:bg-primary-600 disabled:bg-gray-400',
      secondary:
        'bg-primary-50 text-primary-500 hover:bg-primary-100 hover:text-primary-600 disabled:bg-gray-50 disabled:text-gray-400',
      outline:
        'bg-transparent border border-primary-500 text-primary-500 hover:border-primary-600 hover:text-primary-600 disabled:text-gray-400 disabled:hover:text-gray-400 disabled:bg-transparent disabled:border-gray-400',
      error:
        'bg-red-500 text-base-white hover:bg-red-600 disabled:bg-gray-400 focus:!outline-red-300',
      text: 'bg-transparent text-primary-500 hover:text-primary-600 !py-0 !px-[.5rem] disabled:text-gray-400 !h-fit !outline-none',
    },
    size: {
      md: 'h-10 py-2 px-4 rounded',
      sm: 'h-8 py-1 px-2 rounded-sm text-sm',
      xs: 'h-7 p-1 rounded-sm text-sm',
      xxs: 'h-6 w-6 rounded-xs text-xs',
    },
    isIconOnly: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      isIconOnly: true,
      size: 'sm',
      class: 'p-1',
    },
    {
      isIconOnly: true,
      size: 'md',
      class: 'p-2',
    },
    {
      isIconOnly: true,
      variant: 'text',
      class: '!p-0',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    size: 'md',
    isIconOnly: false,
  },
});

export type ButtonProps = AriaButtonProps &
  VariantProps<typeof buttonVariants> & {
    isIconOnly?: boolean;
    isLoading?: boolean;
    rightSection?: JSX.Element;
    leftSection?: JSX.Element;
    className?: ClassValue;
    dataTestId?: string;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      isIconOnly,
      isLoading,
      className,
      leftSection: leftSectionFromProps,
      rightSection,
      children,
      ...props
    },
    forwardedRef,
  ) => {
    const leftSection = isLoading ? (
      <Loader2 className="h-5 w-5 animate-spin" />
    ) : (
      leftSectionFromProps
    );

    return (
      <UnstyledButton
        className={buttonVariants({
          variant,
          size,
          isIconOnly,
          className,
        })}
        data-loading={isLoading}
        ref={forwardedRef}
        data-testid={props.dataTestId}
        {...props}
      >
        {leftSection}
        {children}
        {rightSection}
      </UnstyledButton>
    );
  },
);

Button.displayName = 'Button';
